import {Button, Tag, Text, Toggle} from '@/components-v2'
import {SegmentGroup} from '@/features/Segments/SegmentGroup'
import {SegmentModal} from '@/features/Segments/SegmentModal'
import {useSegmentsOperations} from '@/hooks/promotion/segments/useSegmentsOperations'
import {useState} from 'react'

const defaultModalState = {
  show: false,
  type: '',
  groupIndex: null,
}

export const PromotionSegmentSection = () => {
  const {
    handleAllCustomersToggle,
    handleExcludeToggle,
    isAllCustomers,
    isExcludeEnabled,
    segmentConfiguration,
    handleAddGroup,
    handleSubmitSegments,
    handleDeleteGroup,
    handleRemove,
  } = useSegmentsOperations()

  const [segmentModalState, setSegmentModalState] = useState(defaultModalState)

  const includedSegmentGroups = segmentConfiguration?.includedSegments || []

  const handleSegmentSelection = (items, groupIndex = null) => {
    const type = items?.[0]?.id
    if (!type) return
    setSegmentModalState({show: true, type, groupIndex})
  }

  const handleCloseSegmentModal = () => {
    setSegmentModalState(defaultModalState)
  }

  const handleSegmentSubmission = (selectedSegments, groupIndex, type) => {
    handleSubmitSegments(selectedSegments, groupIndex, type)
    handleCloseSegmentModal()
  }

  return (
    <div className="flex flex-col gap-6 rounded-lg bg-[var(--color-background-layer-01)] p-6">
      <Text headingLevel={5} variant="bold">
        Segments
      </Text>

      <Toggle
        onUpdate={handleAllCustomersToggle}
        checked={!!isAllCustomers}
        size="small"
        label="All Customers"
      />

      {!isAllCustomers && (
        <>
          {includedSegmentGroups.map((segmentGroup, index) => (
            <div key={`group-${index}`}>
              <SegmentGroup
                groupIndex={index}
                segmentGroup={segmentGroup}
                onSegmentSelection={items => handleSegmentSelection(items, index)}
                handleDeleteGroup={handleDeleteGroup}
                handleRemove={handleRemove}
              />
              <div className="my-4 h-px bg-[var(--secondary-800)]" />
            </div>
          ))}

          {includedSegmentGroups.length > 1 && (
            <div className="flex flex-wrap items-center gap-3">
              {includedSegmentGroups.map((_, index) => (
                <div key={`group-label-${index}`} className="flex items-center gap-3">
                  <Tag text={`Group ${index + 1}`} color="blue" />
                  {index < includedSegmentGroups.length - 1 && (
                    <Text size="small" variant="bold">
                      OR
                    </Text>
                  )}
                </div>
              ))}
            </div>
          )}

          <Button
            variant="tertiary"
            size="medium"
            text="Add Group"
            iconLeading="add_box"
            onClick={handleAddGroup}
          />

          <div className="flex flex-col gap-1">
            <Text size="small">Within one group, users must be in all segments to see the promotion.</Text>
            <Text size="small">When there are multiple groups, users can be in either group to see the promotion.</Text>
          </div>
        </>
      )}

      <div className="h-px bg-[var(--secondary-800)]" />

      <Toggle
        onUpdate={handleExcludeToggle}
        checked={!!isExcludeEnabled}
        size="small"
        label="Exclude"
      />

      {isExcludeEnabled && (
        <SegmentGroup
          groupIndex={null}
          segmentGroup={segmentConfiguration?.excludedSegment}
          onSegmentSelection={items => handleSegmentSelection(items, null)}
          handleDeleteGroup={handleDeleteGroup}
          handleRemove={handleRemove}
        />
      )}

      <SegmentModal
        key={`segment-modal-${segmentModalState.show}-${segmentModalState.type}-${segmentModalState.groupIndex}`}
        show={segmentModalState.show}
        type={segmentModalState.type}
        groupIndex={segmentModalState.groupIndex}
        existingSelections={
          segmentModalState.groupIndex === null
            ? (segmentConfiguration?.excludedSegment?.segments ?? [])
            : (segmentConfiguration?.includedSegments?.[segmentModalState.groupIndex]?.segments ?? [])
        }
        onSubmit={handleSegmentSubmission}
        onClose={handleCloseSegmentModal}
      />
    </div>
  )
}
